import type { CornerDotType, CornerSquareType, DotType, Options } from 'qr-code-styling';

/**
 * The designed QR code — ONE description of a code (`QrDesign`), ONE mapping onto `qr-code-styling`
 * (`buildQrOptions`), and the three presets every Symphony surface draws a profile link with
 * (docs/symphony-profile-links-design.md §9.1, D8: "QR style (Symphony mark centred)" is part of the fixed
 * mark, the owner varies only the colour).
 *
 * Ported from wixel-slides — `packages/wixel-components/src/qr/{qr-types,qr-options,qr-fill,qr-constants,
 * qr-contrast}.ts` on origin/master, the same library at the same version (`qr-code-styling` ^1.9.2) — so a
 * design written here opens as-is in the slides editor's QR panel, and a code a presenter puts on a slide
 * built there looks like the one on the landing page. Kept deliberately to the subset a profile link uses:
 * solid fills (no gradients, no background image), URL payloads, a built-in logo or none. Widen it by
 * porting more of the original, not by inventing a second mapping.
 *
 * Pure: no DOM, no import of the library at runtime (types only), so the mapping and the contrast rule are
 * unit-testable in node and the module is safe in an SSR render. `components/symphony/profile-links/
 * DesignedQr.tsx` is the one place that instantiates the library, in the browser.
 */

export type BodyShape = 'square' | 'dots' | 'rounded' | 'extra-rounded' | 'classy' | 'classy-rounded';
export type EyeBorderShape = BodyShape | 'circle';
export type EyeCenterShape = BodyShape | 'circle';

/** A paint for one part of the code: a `#rrggbb` hex and the panel's 0-100 opacity. */
export interface QrFill {
  color: string;
  opacity: number;
}

/** The logo slot. Wixel's union also names its preset glyphs; a profile link only ever uses the mark or nothing. */
export type QrLogo = 'none' | { url: string };

export interface QrDesign {
  payload: { kind: 'url'; value: string };
  body: { shape: BodyShape; fill: QrFill };
  eye: {
    border: EyeBorderShape;
    center: EyeCenterShape;
    borderFill: QrFill;
    centerFill: QrFill;
    /** Eyes follow the body ink until edited in the slides panel; then this latches. */
    colorsDecoupled: boolean;
  };
  logo: { kind: QrLogo; uploads: string[]; sizePct: number; clearSpace: boolean };
  background: QrFill;
}

export const OPAQUE = 100;

export const ERROR_CORRECTION = { DEFAULT: 'M', WITH_LOGO: 'H' } as const;

export const LOGO = { MIN_PCT: 5, MAX_PCT: 30, DEFAULT_PCT: 22, WARN_PCT: 30, MARGIN_MODULES: 2 } as const;

/**
 * Wixel's comment, kept verbatim in substance: a decoder binarizes the image and needs far less separation
 * than reading text does. ISO/IEC 15415 puts usable module contrast near 2:1, so 4:1 is comfortable and 2.5:1
 * is the floor below which a code is genuinely at risk.
 */
export const CONTRAST = { PASS_RATIO: 4, BORDERLINE_RATIO: 2.5 } as const;

export const solidFill = (color: string): QrFill => ({ color, opacity: OPAQUE });

const toCornerSquareType = (shape: EyeBorderShape): CornerSquareType => (shape === 'circle' ? 'dot' : shape) as CornerSquareType;
const toCornerDotType = (shape: EyeCenterShape): CornerDotType => (shape === 'circle' ? 'dot' : shape) as CornerDotType;

export const clampOpacity = (opacity: number): number => (Number.isFinite(opacity) ? Math.min(Math.max(opacity, 0), OPAQUE) : OPAQUE);

/** Folds the opacity into a 6-digit hex as an alpha suffix; anything else is left alone rather than corrupted. */
export const withOpacity = (color: string, opacity: number): string => {
  const clamped = clampOpacity(opacity);
  if (clamped >= OPAQUE) return color;
  const alpha = Math.round((clamped / OPAQUE) * 255)
    .toString(16)
    .padStart(2, '0');
  return /^#[0-9a-f]{6}$/i.test(color) ? `${color}${alpha}` : color;
};

export const resolveEyeColors = (design: QrDesign): { border: QrFill; center: QrFill } =>
  design.eye.colorsDecoupled
    ? { border: design.eye.borderFill, center: design.eye.centerFill }
    : { border: design.body.fill, center: design.body.fill };

const logoImage = (design: QrDesign): string | undefined => (design.logo.kind === 'none' ? undefined : design.logo.kind.url || undefined);

/** `QrDesign` → the library's options. Same field-for-field mapping as wixel's `buildQrOptions`. */
export function buildQrOptions({ design, size }: { design: QrDesign; size: number }): Options {
  const eyeColors = resolveEyeColors(design);
  const image = logoImage(design);
  return {
    type: 'svg',
    width: size,
    height: size,
    margin: 0,
    data: design.payload.value,
    image,
    imageOptions: {
      imageSize: design.logo.sizePct / 100,
      margin: LOGO.MARGIN_MODULES,
      hideBackgroundDots: design.logo.clearSpace,
      crossOrigin: 'anonymous',
    },
    qrOptions: {
      // A logo occludes modules, so buy the redundancy back with the highest ECC.
      errorCorrectionLevel: image ? ERROR_CORRECTION.WITH_LOGO : ERROR_CORRECTION.DEFAULT,
    },
    dotsOptions: { type: design.body.shape as DotType, color: withOpacity(design.body.fill.color, design.body.fill.opacity) },
    cornersSquareOptions: { type: toCornerSquareType(design.eye.border), color: withOpacity(eyeColors.border.color, eyeColors.border.opacity) },
    cornersDotOptions: { type: toCornerDotType(design.eye.center), color: withOpacity(eyeColors.center.color, eyeColors.center.opacity) },
    backgroundOptions: { color: withOpacity(design.background.color, design.background.opacity) },
  };
}

// ---- contrast (wixel `qr-contrast.ts`, solid fills only) ----

interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** `#rgb` or `#rrggbb` → channels; anything else is null. */
export function parseHexColor(value: string): Rgb | null {
  const match = String(value).trim().match(/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
  if (!match) return null;
  let hex = match[1];
  if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  return { r: parseInt(hex.slice(0, 2), 16), g: parseInt(hex.slice(2, 4), 16), b: parseInt(hex.slice(4, 6), 16) };
}

export function relativeLuminance({ r, g, b }: Rgb): number {
  const channel = (value: number) => {
    const s = value / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** The ratio between two hex colours, or null when either is not one. */
export function hexContrast(a: string, b: string): number | null {
  const pa = parseHexColor(a);
  const pb = parseHexColor(b);
  return pa && pb ? contrastRatio(pa, pb) : null;
}

export type ContrastLevel = 'pass' | 'borderline' | 'fail';

/** The module ink against the plate — the number a scanner cares about. */
export function getContrastRatio(design: QrDesign): number | null {
  return hexContrast(design.body.fill.color, design.background.color);
}

export function getContrastLevel(design: QrDesign): ContrastLevel {
  const ratio = getContrastRatio(design);
  if (ratio === null) return 'borderline';
  const logoOversized = design.logo.kind !== 'none' && design.logo.sizePct > LOGO.WARN_PCT;
  if (ratio < CONTRAST.BORDERLINE_RATIO) return 'fail';
  if (ratio < CONTRAST.PASS_RATIO || logoOversized) return 'borderline';
  return 'pass';
}

const toHex = ({ r, g, b }: Rgb): string => `#${[r, g, b].map((c) => Math.round(c).toString(16).padStart(2, '0')).join('')}`;

/**
 * `color`, moved toward black or white just far enough to reach `minRatio` against `against`. Adjusted,
 * never refused (design §5.2): an owner's light accent stays their accent on the page and becomes a dark
 * shade of itself in the code. The direction is whichever end is further from `against`, so a light plate
 * darkens the ink and a dark page lightens the text. An unparseable input returns the extreme it would
 * have been pushed to.
 */
export function adjustToContrast(color: string, against: string, minRatio: number): string {
  const base = parseHexColor(color);
  const ground = parseHexColor(against);
  const towardBlack = !ground || relativeLuminance(ground) > 0.18;
  const target: Rgb = towardBlack ? { r: 0, g: 0, b: 0 } : { r: 255, g: 255, b: 255 };
  if (!base || !ground) return toHex(target);
  if (contrastRatio(base, ground) >= minRatio) return toHex(base);
  for (let step = 1; step <= 20; step++) {
    const t = step / 20;
    const mixed = { r: base.r + (target.r - base.r) * t, g: base.g + (target.g - base.g) * t, b: base.b + (target.b - base.b) * t };
    const rounded = { r: Math.round(mixed.r), g: Math.round(mixed.g), b: Math.round(mixed.b) };
    if (contrastRatio(rounded, ground) >= minRatio) return toHex(rounded);
  }
  return toHex(target);
}

// ---- the three presets ----

/* EXEMPT — the QR code is an IMAGE, not themed UI: it is exported as SVG/PNG, put on slides and printed, so
   its inks are fixed literals that never follow light/dark mode. Values: --ds-neutral-900 ink on --ds-white,
   and the brand mark exactly as public/symphony-mark.svg draws it (pinned by test/qr-design.test.ts). */
const INK = '#1f1f1f';
const PLATE = '#ffffff';
const PROJECTOR_INK = '#000000';
const MARK_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none">' +
  '<rect x="1" y="1" width="22" height="22" rx="6.5" fill="#A4FF70" />' +
  '<rect x="10.435" y="3.135" width="3.65" height="3.65" rx="0.24" fill="#333333" />' +
  '<rect x="15.775" y="5.845" width="2.57" height="2.57" rx="1.29" fill="#333333" />' +
  '<rect x="17.235" y="10.305" width="3.65" height="3.65" rx="1.83" fill="#333333" />' +
  '<rect x="15.245" y="15.345" width="3.65" height="3.65" rx="1.58" fill="#333333" />' +
  '<rect x="10.435" y="17.475" width="3.65" height="3.65" rx="1.28" fill="#333333" />' +
  '<rect x="5.195" y="15.345" width="3.65" height="3.65" rx="1.04" fill="#333333" />' +
  '<rect x="3.055" y="10.305" width="3.65" height="3.65" rx="0.79" fill="#333333" />' +
  '<rect x="5.195" y="5.315" width="3.65" height="3.65" rx="0.55" fill="#333333" />' +
  '</svg>';
/* end EXEMPT */

export const SYMPHONY_MARK_SVG = MARK_SVG;

/**
 * The mark as a data URI rather than a `/symphony-mark.svg` URL: the app has no basePath (a mount prefix
 * would have to be threaded in), an exported SVG must carry its logo inline to render anywhere, and a
 * server render has no origin to fetch from.
 */
export const SYMPHONY_MARK_DATA_URI = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(MARK_SVG)}`;

/** The logo share of the code. Under the 30% warning line, with ECC H buying back what it covers. */
const MARK_SIZE_PCT = 24;

export type QrPreset = 'symphony' | 'owner' | 'projector';

const designed = (value: string, ink: string): QrDesign => ({
  payload: { kind: 'url', value },
  body: { shape: 'rounded', fill: solidFill(ink) },
  eye: { border: 'extra-rounded', center: 'circle', borderFill: solidFill(ink), centerFill: solidFill(ink), colorsDecoupled: false },
  logo: { kind: { url: SYMPHONY_MARK_DATA_URI }, uploads: [], sizePct: MARK_SIZE_PCT, clearSpace: true },
  background: solidFill(PLATE),
});

/** Rounded modules, extra-rounded eyes, dark ink on white, the Symphony mark centred, ECC H. */
export function symphonyQrDesign(url: string): QrDesign {
  return designed(url, INK);
}

/**
 * The same shapes and mark, inked in the owner's accent darkened until it clears the 4:1 pass ratio against
 * the white plate. A light accent (the brand green, a pastel) would otherwise fail the library's own floor.
 * An accent that is not `#rrggbb` falls back to the Symphony ink.
 */
export function ownerQrDesign(url: string, accent: string): QrDesign {
  const ink = /^#[0-9a-f]{6}$/i.test(accent) ? adjustToContrast(accent, PLATE, CONTRAST.PASS_RATIO) : INK;
  return designed(url, ink);
}

/** For a very large room or a weak projector: plain square modules, black on white, no logo, ECC M. */
export function projectorQrDesign(url: string): QrDesign {
  return {
    payload: { kind: 'url', value: url },
    body: { shape: 'square', fill: solidFill(PROJECTOR_INK) },
    eye: { border: 'square', center: 'square', borderFill: solidFill(PROJECTOR_INK), centerFill: solidFill(PROJECTOR_INK), colorsDecoupled: false },
    logo: { kind: 'none', uploads: [], sizePct: LOGO.DEFAULT_PCT, clearSpace: true },
    background: solidFill(PLATE),
  };
}

export function qrDesignFor(preset: QrPreset, url: string, accent?: string): QrDesign {
  if (preset === 'projector') return projectorQrDesign(url);
  if (preset === 'owner' && accent) return ownerQrDesign(url, accent);
  return symphonyQrDesign(url);
}
